/**
 * vue-cesium - https://github.com/zouyaoji/vue-cesium
 * Vue 2.x components for CesiumJS.
 *
 * @package VueCesium
 * @author zouyaoji <370681295@qq.com>
 * @version 2.0.6
 * @license MIT
 * @homepage https://zouyaoji.top/vue-cesium
 * @copyright (c) 2018-2020, zouyaoji <370681295@qq.com>
 */
import _Object$defineProperties from '@babel/runtime-corejs2/core-js/object/define-properties';
import _Object$assign from '@babel/runtime-corejs2/core-js/object/assign';
import _regeneratorRuntime from '@babel/runtime-corejs2/regenerator';
import _getIterator from '@babel/runtime-corejs2/core-js/get-iterator';
import cmp from '../virtualCmp';
import { makeColor } from '../../utils/util';

var props = {
  mode: {
    type: Number,
    default: 1
  },
  font: {
    type: String,
    default: '100 20px SimSun'
  },
  fillColor: {
    type: String | Object | Array,
    default: 'WHITE'
  },
  labelStyle: {
    type: Number,
    default: 2
  },
  showBackground: {
    type: Boolean,
    default: true
  },
  backgroundColor: {
    type: String | Object | Array,
    default: 'rgba(38, 38, 38, 0.85)'
  },
  outlineWidth: {
    type: Number,
    default: 1
  },
  outlineColor: {
    type: String | Object | Array,
    default: 'BLUE'
  },
  pixelOffset: {
    type: Object,
    default: function _default() {
      return {
        x: 15,
        y: -20
      };
    }
  },
  pointColor: {
    type: String | Object | Array,
    default: 'rgb(255,229,0)'
  },
  pointPixelSize: {
    type: Number,
    default: 8
  },
  polylineColor: {
    type: String | Object | Array,
    default: '#51ff00'
  },
  polylineWidth: {
    type: Number,
    default: 2
  },
  depthTest: {
    type: Boolean,
    default: false
  }
};
var watch = {
  measuring: function measuring(val) {
    var polylines = this.polylines,
        startNew = this.startNew,
        type = this.type;
    var polyline = polylines[polylines.length - 1];

    if (!val && polyline && !polyline.positions.length) {
      this.polylines.pop();
    } else if (val) {
      var measureCmpNames = [];

      switch (type) {
        case 'distanceMeasuring':
          measureCmpNames.push('vc-measure-height');
          measureCmpNames.push('vc-measure-area');
          break;

        case 'areaMeasuring':
          measureCmpNames.push('vc-measure-distance');
          measureCmpNames.push('vc-measure-height');
          break;

        case 'heightMeasuring':
          measureCmpNames.push('vc-measure-distance');
          measureCmpNames.push('vc-measure-area');
          break;
      }

      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = _getIterator(this.$parent.$slots.default || []), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var $node = _step.value;

          if ($node.componentOptions && measureCmpNames.indexOf($node.componentOptions.tag) !== -1) {
            $node.child.measuring = false;
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      startNew();
    }

    this.viewer.canvas.setAttribute('style', val ? 'cursor: crosshair' : 'cursor: auto');
    var listener = this.$listeners['activeEvt'];
    listener && this.$emit('activeEvt', {
      type: type,
      isActive: val
    });
  }
};
var methods = {
  createCesiumObject: function createCesiumObject() {
    var viewer, handler;
    return _regeneratorRuntime.async(function createCesiumObject$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            viewer = this.viewer;
            viewer.scene.frameState.morphTime = 0;
            handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
            handler.setInputAction(this.LEFT_CLICK, Cesium.ScreenSpaceEventType.LEFT_CLICK);
            handler.setInputAction(this.MOUSE_MOVE, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
            handler.setInputAction(this.RIGHT_CLICK, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
            this.handler = handler;
            return _context.abrupt("return", this.polylines);

          case 8:
          case "end":
            return _context.stop();
        }
      }
    }, null, this);
  },
  mount: function mount() {
    return _regeneratorRuntime.async(function mount$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            return _context2.abrupt("return", true);

          case 1:
          case "end":
            return _context2.stop();
        }
      }
    });
  },
  unmount: function unmount() {
    return _regeneratorRuntime.async(function unmount$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            return _context3.abrupt("return", true);

          case 1:
          case "end":
            return _context3.stop();
        }
      }
    });
  },
  LEFT_CLICK: function LEFT_CLICK(movement) {
    if (!this.measuring) {
      return;
    }

    var Cesium = this.Cesium,
        viewer = this.viewer,
        polylines = this.polylines,
        type = this.type;
    var cartesian = viewer.scene.pickPosition(movement.position);

    if (!Cesium.defined(cartesian)) {
      return;
    }

    var polyline = polylines[polylines.length - 1];

    switch (type) {
      case 'distanceMeasuring':
        polyline.positions.push(cartesian);
        var distance = polyline.distance;
        polyline.distances.push(distance);
        break;

      case 'areaMeasuring':
        polyline.positions.push(cartesian);
        break;

      case 'heightMeasuring':
        if (polyline.positions.length === 0) {
          polyline.positions.push(cartesian);
          this.startPoint = cartesian;
        }

        break;
    }
  },
  MOUSE_MOVE: function MOUSE_MOVE(movement) {
    var viewer, polylines, onMeasureEvt, type, nIndex, polyline, cartesian, distance, labels, endPoint, normalStart, planeStart, hypPoint, labelPositonHeight, labelPositonH, labelPositonS, normalEnd, planeEnd, labelTextHeight, labelTextH, labelTextS;
    return _regeneratorRuntime.async(function MOUSE_MOVE$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            if (this.measuring) {
              _context4.next = 2;
              break;
            }

            return _context4.abrupt("return");

          case 2:
            viewer = this.viewer, polylines = this.polylines, onMeasureEvt = this.onMeasureEvt, type = this.type;

            if (polylines.length) {
              _context4.next = 5;
              break;
            }

            return _context4.abrupt("return");

          case 5:
            nIndex = polylines.length - 1;
            polyline = polylines[nIndex];

            if (polyline.positions.length) {
              _context4.next = 9;
              break;
            }

            return _context4.abrupt("return");

          case 9:
            cartesian = viewer.scene.pickPosition(movement.endPosition);

            if (Cesium.defined(cartesian)) {
              _context4.next = 12;
              break;
            }

            return _context4.abrupt("return");

          case 12:
            if (!(type === 'distanceMeasuring' || type === 'areaMeasuring')) {
              _context4.next = 21;
              break;
            }

            if (polyline.positions.length >= 2) {
              polyline.positions.pop();
              polyline.distances && polyline.distances.pop();
            }

            polyline.positions.push(cartesian);

            if (type === 'distanceMeasuring') {
              distance = this.getDistance(polyline.positions);
              polyline.distances.push(distance);
              polyline.distance = distance;
              nIndex = polylines.reduce(function (pre, cur) {
                return pre + cur.positions.length - 1;
              }, 0) - 1;
            } else {
              polyline.area = this.getSurfaceArea(polyline.positions);
              polyline.projectedArea = this.getProjectedArea(polyline.positions);
            }

            _context4.next = 18;
            return _regeneratorRuntime.awrap(this.$nextTick());

          case 18:
            onMeasureEvt(polyline, nIndex);
            _context4.next = 47;
            break;

          case 21:
            labels = this.labels;
            endPoint = cartesian;
            normalStart = {};
            Cesium.Cartesian3.normalize(this.startPoint, normalStart);
            planeStart = new Cesium.Plane(normalStart, -Cesium.Cartesian3.distance(this.startPoint, new Cesium.Cartesian3(0, 0, 0)));
            hypPoint = {};
            polyline.height = Cesium.Plane.getPointDistance(planeStart, endPoint);
            labelPositonHeight = {};
            labelPositonH = {};
            labelPositonS = {};

            if (polyline.height >= 0) {
              Cesium.Plane.projectPointOntoPlane(planeStart, endPoint, hypPoint);
              Cesium.Cartesian3.midpoint(endPoint, hypPoint, labelPositonHeight);
              Cesium.Cartesian3.midpoint(this.startPoint, hypPoint, labelPositonH);
              polyline.distanceH = Cesium.Cartesian3.distance(this.startPoint, hypPoint);
            } else {
              normalEnd = {};
              Cesium.Cartesian3.normalize(endPoint, normalEnd);
              planeEnd = new Cesium.Plane(normalStart, -Cesium.Cartesian3.distance(endPoint, new Cesium.Cartesian3(0, 0, 0)));
              Cesium.Plane.projectPointOntoPlane(planeEnd, this.startPoint, hypPoint);
              Cesium.Cartesian3.midpoint(this.startPoint, hypPoint, labelPositonHeight);
              Cesium.Cartesian3.midpoint(endPoint, hypPoint, labelPositonH);
              polyline.distanceH = Cesium.Cartesian3.distance(endPoint, hypPoint);
            }

            polyline.distanceS = Cesium.Cartesian3.distance(this.startPoint, endPoint);
            Cesium.Cartesian3.midpoint(this.startPoint, endPoint, labelPositonS);
            polyline.height = Math.abs(polyline.height);

            if (polyline.positions.length !== 1) {
              polyline.positions.pop();
              polyline.positions.pop();
              labels.pop();
              labels.pop();
              labels.pop();
            }

            polyline.positions.push(endPoint);
            polyline.positions.push(hypPoint);
            labelTextHeight = polyline.height > 1000 ? (polyline.height / 1000).toFixed(2) + 'km' : polyline.height.toFixed(2) + 'm';
            labels.push({
              text: this.$vc.lang.measure.verticalHeight + ': ' + labelTextHeight,
              position: labelPositonHeight
            });
            labelTextH = polyline.distanceH > 1000 ? (polyline.distanceH / 1000).toFixed(2) + 'km' : polyline.distanceH.toFixed(2) + 'm';
            labels.push({
              text: this.$vc.lang.measure.horizontalDistance + ': ' + labelTextH,
              position: labelPositonH
            });
            labelTextS = polyline.distanceS > 1000 ? (polyline.distanceS / 1000).toFixed(2) + 'km' : polyline.distanceS.toFixed(2) + 'm';
            labels.push({
              text: this.$vc.lang.measure.spaceDistance + ': ' + labelTextS,
              position: labelPositonS
            });
            _context4.next = 46;
            return _regeneratorRuntime.awrap(this.$nextTick());

          case 46:
            onMeasureEvt(polyline, labels);

          case 47:
          case "end":
            return _context4.stop();
        }
      }
    }, null, this);
  },
  RIGHT_CLICK: function RIGHT_CLICK(movement) {
    var viewer, polylines, mode, startNew, onMeasureEvt, type, nIndex, polyline, cartesian, getDistance, getSurfaceArea, getProjectedArea, labels;
    return _regeneratorRuntime.async(function RIGHT_CLICK$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            if (this.measuring) {
              _context5.next = 2;
              break;
            }

            return _context5.abrupt("return");

          case 2:
            viewer = this.viewer, polylines = this.polylines, mode = this.mode, startNew = this.startNew, onMeasureEvt = this.onMeasureEvt, type = this.type;

            if (polylines.length) {
              _context5.next = 5;
              break;
            }

            return _context5.abrupt("return");

          case 5:
            nIndex = polylines.length - 1;
            polyline = polylines[nIndex];

            if (!(polyline.positions.length === 0)) {
              _context5.next = 9;
              break;
            }

            return _context5.abrupt("return");

          case 9:
            cartesian = viewer.scene.pickPosition(movement.position);

            if (Cesium.defined(cartesian)) {
              _context5.next = 12;
              break;
            }

            return _context5.abrupt("return");

          case 12:
            if (type === 'distanceMeasuring') {
              polyline.positions.pop();
              polyline.distances.pop();
              getDistance = this.getDistance;
              polyline.distance = getDistance(polyline.positions);

              if (polyline.positions.length === 1) {
                polyline.positions = [];
              }

              nIndex = polylines.reduce(function (pre, cur) {
                return pre + cur.positions.length - 1;
              }, 0) - 1;
            } else if (type === 'areaMeasuring') {
              polyline.positions.pop();
              getSurfaceArea = this.getSurfaceArea, getProjectedArea = this.getProjectedArea;
              polyline.area = getSurfaceArea(polyline.positions);
              polyline.projectedArea = getProjectedArea(polyline.positions);

              if (polyline.positions.length <= 2) {
                polyline.positions = [];
              }
            }

            if (mode === 0) {
              startNew();
            } else {
              this.measuring = false;
            }

            _context5.next = 16;
            return _regeneratorRuntime.awrap(this.$nextTick());

          case 16:
            if (type === 'distanceMeasuring' || type === 'areaMeasuring') {
              onMeasureEvt(polyline, nIndex, true);
            } else {
              labels = this.labels;
              onMeasureEvt(polyline, labels, true);
            }

          case 17:
          case "end":
            return _context5.stop();
        }
      }
    }, null, this);
  },
  startNew: function startNew() {
    var polylines = this.polylines,
        type = this.type;

    if (!Cesium.defined(polylines)) {
      return;
    }

    var polyline = {
      positions: [],
      materialLine: new Cesium.Material({
        fabric: {
          type: 'Color',
          uniforms: {
            color: makeColor(this.polylineColor)
          }
        }
      })
    };

    if (type === 'distanceMeasuring') {
      _Object$assign(polyline, {
        distances: [],
        distance: 0
      });
    } else if (type === 'areaMeasuring') {
      _Object$assign(polyline, {
        area: 0,
        projectedArea: 0
      });
    } else {
      _Object$assign(polyline, {
        distanceH: 0,
        height: 0,
        distanceS: 0
      });
    }

    polylines.push(polyline);
  },
  onMeasureEvt: function onMeasureEvt(polyline, index) {
    var flag = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

    if (!this.depthTest) {
      this.$refs.polylineCollection && (this.$refs.polylineCollection.cesiumObject._opaqueRS.depthTest.enabled = false);
      var rs = Cesium.RenderState.fromCache({
        depthMask: true,
        depthTest: {
          enabled: false
        }
      });
      this.$refs.labelCollection.cesiumObject._billboardCollection._rsTranslucent = rs;
      this.$refs.labelCollection.cesiumObject._backgroundBillboardCollection._rsTranslucent = rs;
      var listener = this.$listeners['measureEvt'];
      var type = this.type;

      if (type === 'distanceMeasuring' || type === 'areaMeasuring') {
        listener && this.$emit('measureEvt', {
          polyline: polyline,
          label: this.$refs.labelCollection.cesiumObject.get(index),
          type: type,
          finished: flag
        });
      } else {
        var labels = index;
        var labelsResult = {
          labelHeight: this.$refs.labelCollection.cesiumObject.get(labels.length - 3),
          labelH: this.$refs.labelCollection.cesiumObject.get(labels.length - 2),
          labelS: this.$refs.labelCollection.cesiumObject.get(labels.length - 1)
        };
        listener && this.$emit('measureEvt', {
          polyline: polyline,
          label: labelsResult,
          type: 'heightMeasuring',
          finished: flag
        });
      }
    }
  }
};
var mixinMeasure = {
  mixins: [cmp],
  props: props,
  watch: watch,
  methods: methods,
  created: function created() {
    var _this = this;

    _Object$defineProperties(this, {
      polyline: {
        enumerable: true,
        get: function get() {
          return _this.polyline;
        }
      }
    });
  },
  destroyed: function destroyed() {
    var handler = this.handler;

    if (handler) {
      handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK);
    }
  }
};

export default mixinMeasure;
